import {
  AfterViewChecked, ChangeDetectionStrategy, ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges, OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {DxDropDownBoxComponent} from 'devextreme-angular';
import {DxDataGridComponent} from 'devextreme-angular';
import {BehaviorSubject, Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {ScreenService} from '../../../../shared/services/general';
import {BillType} from '../../models/billing/bill-type';
import {BillingInformation} from '../../models/billing/billing-information';
import {Payer} from '../../models/billing/payer';
import {PatientEditable} from '../../models/order-entry/patient';
import {BillingService} from '../../services/billing.service';
import DataSource, { DataSourceOptions } from 'devextreme/data/data_source';
import { Store } from 'devextreme/data';
import * as Sentry from '@sentry/angular-ivy';

@Component({
  selector: 'app-billing-information',
  templateUrl: './billing-information.component.html',
  styleUrls: ['./billing-information.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BillingInformationComponent implements OnInit, OnChanges, AfterViewChecked, OnDestroy {
  @ViewChild(DxDataGridComponent, {static: true}) public billingInfoGrid?: DxDataGridComponent;
  @ViewChild('payerDropDown', {static: true}) public payerSelector?: DxDropDownBoxComponent;
  @ViewChild('container', {static: true}) public containerDiv?: ElementRef;
  @ViewChild('controls', {static: true}) public controlsDiv?: ElementRef;
  @Input() public patientBillingInformation: DataSource | DataSourceOptions | Store | null | string | Array<any> = [] as any[];
  @Input() public maxHeight: string | number = '100%';
  @Output() public patientBillingInfoChanged = new EventEmitter();
  @Input() public patient?: PatientEditable;
  @Input() public deselectAll ? = new EventEmitter();
  @Input() public gridHeight?: number | string;
  // @ts-ignore
  public billTypesDataSource: BehaviorSubject<DataSource> = new BehaviorSubject<DataSource>(null);
  // @ts-ignore
  public payersDataSource: BehaviorSubject<DataSource> = new BehaviorSubject<DataSource>(null);
  public isLoading = false;
  public selectedBillType?: BillType;
  public selectedPayer?: Payer;
  public selectedRows: BillingInformation[] = [];
  public insuranceId = '';
  public groupNumber = '';
  public isEditVisible = false;
  public editingRowInfo: any;
  public responsiveButtons: any;
  public isPhone: boolean = false
  public isPhoneOrTablet: boolean = false;
  public selectedRowIndex: number = -1;
  private onDestroy$: Subject<void> = new Subject<void>();

  constructor(private billingInfoService: BillingService, private screen: ScreenService, private ref: ChangeDetectorRef) {
    this.responsiveButtons = [{
      name: 'Add',
      icon: 'add',
      disabled: true,
    }, {
      name: 'Remove',
      icon: 'trash',
      disabled: true,
    }, {
      name: 'Up',
      icon: 'arrowup',
      disabled: true,
    }, {
      name: 'Save',
      icon: 'save',
      disabled: true,
    }, {
      name: 'Down',
      icon: 'arrowdown',
      disabled: true,
    },
    ];
    this.payerChanged = this.payerChanged.bind(this);
  }

  public ngAfterViewChecked(): void {
    if (this.billingInfoGrid)
      this.billingInfoGrid.height = this.GridSize;

    if (typeof this.maxHeight === 'number' && this.controlsDiv) {
      this.gridHeight = this.maxHeight - this.controlsDiv.nativeElement.clientHeight - 32;
    } else {
      this.gridHeight = '100%';
    }

    this.refresh();
  }

  public get GridSize() {
    const element = this.containerDiv?.nativeElement as HTMLElement;
    return element?.getBoundingClientRect().height -
      32 - element?.getBoundingClientRect().height;
  }

  public ngOnChanges(changes: import('@angular/core').SimpleChanges): void {
    if (changes.patient && changes.patient.currentValue) {
      if (changes.patient.currentValue.insuranceInformation) {
        this.patientBillingInformation = changes.patient.currentValue.insuranceInformation;
        this.refresh();
      }
    }
  }

  public closeDialog(billingInfo: BillingInformation): void {
    if (billingInfo) {
      this.billingInfoGrid?.instance.deselectAll()
        .then(() => {
          const index = this.editingRowInfo.rowType === 'detailAdaptive' ? this.editingRowInfo.rowIndex - 1 : this.editingRowInfo.rowIndex;
          this.billingInfoGrid?.instance.selectRowsByIndexes(index)
            .then(() => {
              const row = this.billingInfoGrid?.instance.getSelectedRowsData()[0];
              row.address = billingInfo.address;
              row.differentThanPatient = billingInfo.differentThanPatient;
              row.relationship = billingInfo.relationship;
              row.middleName = billingInfo.middleName;
              row.gender = billingInfo.gender;
              row.phone = billingInfo.phone;
              row.birthDate = billingInfo.birthDate;
              row.zipCodePlus4 = billingInfo.zipCodePlus4;
              row.zipCode = billingInfo.zipCode;
              row.firstName = billingInfo.firstName;
              row.lastName = billingInfo.lastName;
              this.billingInfoGrid?.instance.saveEditData()
                .then(() => {
                  this.billingInfoGrid?.instance.repaint();
                  this.billingInfoGrid?.instance.refresh();
                });
            });
        });
    }

    this.isEditVisible = false;
    this.patientBillingInfoChanged.emit(this.patientBillingInformation);
    this.refresh();
  }

  public ngOnInit() {
    this.billTypesDataSource.next(new DataSource({store: this.billingInfoService.billingInfoCustomStore(this.billTypesDataSource)}));
    this.payersDataSource.next(new DataSource({store: this.billingInfoService.payerCustomStore(0, false, this.payersDataSource)}));

    this.billTypeChanged = this.billTypeChanged.bind(this);
    this.addBtnClicked = this.addBtnClicked.bind(this);
    this.removeBtnClicked = this.removeBtnClicked.bind(this);
    this.changeDown = this.changeDown.bind(this);
    this.changeUp = this.changeUp.bind(this);

    this.isPhone = this.screen.sizes['screen-x-small'];
    this.isPhoneOrTablet = this.screen.sizes['screen-x-small'] || this.screen.sizes['screen-small'] || this.screen.sizes['screen-tablet'];
    this.screen.changed.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      this.isPhone = this.screen.sizes['screen-x-small'];
      this.isPhoneOrTablet = this.screen.sizes['screen-x-small'] || this.screen.sizes['screen-small'] || this.screen.sizes['screen-tablet'];
      this.refresh();
    });

    this.deselectAll?.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      this.selectedRows = [];
      this.refresh();
    });
    this.refresh();
  }

  public billTypeChanged(event: any) {
    if (event.value) {
      this.payersDataSource.next(new DataSource({store: this.billingInfoService.payerCustomStore(event.value.id, event.value.allPayers, this.payersDataSource)}));
    }
    this.selectedPayer = undefined;
    this.changeStateButtons();
    this.refresh();
  }

  public addBtnClicked() {
    if (this.insuranceId !== '' && this.selectedPayer && this.selectedBillType) {
      const billingInfo = new BillingInformation();
      billingInfo.address = this.patient?.address ? this.patient.address.toUpperCase() : '';
      billingInfo.relationship = 'SELF';
      billingInfo.order = (this.patientBillingInformation as any[]).length + 1;
      billingInfo.phone = this.patient?.phone ? this.patient.phone : '';
      billingInfo.differentThanPatient = false;
      billingInfo.firstName = this.patient?.firstName ? this.patient.firstName.toUpperCase() : '';
      billingInfo.lastName = this.patient?.lastName ? this.patient.lastName.toUpperCase() : '';
      billingInfo.middleName = this.patient?.middleName ? this.patient.middleName.toUpperCase() : '';
      billingInfo.gender = this.patient?.gender;
      billingInfo.zipCodePlus4 = this.patient?.zipCodePlus4;
      billingInfo.billType = this.selectedBillType.name;
      billingInfo.billTypeId = this.selectedBillType.id;
      billingInfo.payer = this.selectedPayer.name;
      billingInfo.payerId = this.selectedPayer.id;
      billingInfo.insuranceId = this.insuranceId.toUpperCase();
      billingInfo.groupNumber = this.groupNumber ? this.groupNumber.toUpperCase() : '';
      billingInfo.birthDate = this.patient?.dob ? new Date(this.patient.dob) : '';
      (this.patientBillingInformation as any[]).push(billingInfo);
      this.selectedPayer = undefined;
      this.selectedBillType = undefined;
      this.insuranceId = '';
      this.groupNumber = '';
      this.patientBillingInfoChanged.emit(this.patientBillingInformation);
      this.refresh();
    }
  }

  public removeBtnClicked() {
    this.selectedRows.forEach(x => {
      const index = (this.patientBillingInformation as any[]).indexOf(x, 0);
      (this.patientBillingInformation as any[]).splice(index, 1);
    });
    this.selectedRows = [];
    this.patientBillingInfoChanged.emit(this.calculateOrder((this.patientBillingInformation as any[])));
    this.refresh();
  }

  public calculateOrder(pbi: BillingInformation[]) {
    for (let i = 0; i < pbi.length; i++) {
      pbi[i].order = i + 1;
    }
    return pbi;
  }

  public editBtnClicked(rowInfo: any) {
    this.isEditVisible = true;
    this.editingRowInfo = rowInfo;
    this.assignPatientData(rowInfo.data.differentThanPatient);
    this.refresh();
  }

  public verifyMissingInformation(rowInfo: any): boolean {
    return rowInfo.data.lastName === null || rowInfo.data.firstName === null || rowInfo.data.address === null
      || rowInfo.data.birthDate === null || rowInfo.data.gender === null
      || rowInfo.data.zipCode === null || rowInfo.data.relationship === null;
  }

  public assignPatientData(loadData: boolean) {
    if (!loadData && !(this.editingRowInfo.data as BillingInformation).id) {
      (this.editingRowInfo.data as BillingInformation).lastName = this.patient?.lastName ? this.patient.lastName : '';
      (this.editingRowInfo.data as BillingInformation).firstName = this.patient?.firstName ? this.patient?.firstName : '';
      (this.editingRowInfo.data as BillingInformation).middleName = this.patient?.middleName ? this.patient?.middleName : '';
      (this.editingRowInfo.data as BillingInformation).address = this.patient?.address;
      (this.editingRowInfo.data as BillingInformation).birthDate = (this.patient as any).dob !== null &&
      (this.patient as any).dob !== undefined ? new Date((this.patient as any).dob) : this.patient?.birthDate !== null &&
      this.patient?.birthDate !== undefined ? new Date(this.patient?.birthDate) : '';
      (this.editingRowInfo.data as BillingInformation).gender = this.patient?.gender;
      (this.editingRowInfo.data as BillingInformation).zipCode = this.patient?.zipCode;
      (this.editingRowInfo.data as BillingInformation).zipCodePlus4 = this.patient?.zipCodePlus4;
      (this.editingRowInfo.data as BillingInformation).phone = this.patient?.phone ? this.patient?.phone : '';
      (this.editingRowInfo.data as BillingInformation).relationship = 'SELF';
      this.refresh();
    }
  }

  public reloadPatientData(loadData: boolean) {
    this.assignPatientData(loadData);
  }

  public changeDown() {
    const prev = Math.max(...this.getIndexesOfSelected()) + 1;

    if (prev < (this.patientBillingInformation as any[]).length) {
      (this.patientBillingInformation as any[])[prev].order =
        (this.patientBillingInformation as any[])[Math.min(...this.getIndexesOfSelected())].order;
      // @ts-ignore
      this.getIndexesOfSelected().forEach(index => this.patientBillingInformation[index].order++);
      // @ts-ignore
      this.patientBillingInformation.sort((a, b) => a.order - b.order);
      this.patientBillingInfoChanged.emit(this.patientBillingInformation);
    }
    this.refresh();
  }

  public changeUp() {
    const prev = Math.min(...this.getIndexesOfSelected()) - 1;

    if (prev >= 0) {
      (this.patientBillingInformation as any[])[prev].order =
        (this.patientBillingInformation as any[])[Math.max(...this.getIndexesOfSelected())].order;
      // @ts-ignore
      this.getIndexesOfSelected().forEach(index => this.patientBillingInformation[index].order--);
      // @ts-ignore
      this.patientBillingInformation.sort((a, b) => a.order - b.order);
      this.patientBillingInfoChanged.emit(this.patientBillingInformation);
    }
    this.refresh();
  }

  private getIndexesOfSelected() {
    const indexes: number[] = [];

    this.selectedRows.forEach((rp: any) => {
      const index = (this.patientBillingInformation as any[]).findIndex(r =>
        r.billTypeId === (rp as BillingInformation).billTypeId &&
        r.payerId === (rp as BillingInformation).payerId &&
        r.insuranceId === (rp as BillingInformation).insuranceId &&
        r.groupNumber === (rp as BillingInformation).groupNumber,
      );
      indexes.push(index);
    });

    return indexes;
  }

  public onItemClick(e: any) {
    switch (e.itemData.name) {
      case 'Up':
        this.changeUp();
        break;
      case 'Down':
        this.changeDown();
        break;
      case 'Add':
        this.addBtnClicked();
        break;
      case 'Save':
        this.saveBtnClicked();
        break;
      case 'Remove':
        this.removeBtnClicked();
        break;
    }
  }

  public saveBtnClicked() {
    if (this.insuranceId !== '' && this.selectedPayer && this.selectedBillType) {
      (this.patientBillingInformation as any[])[this.selectedRowIndex].payer = this.selectedPayer.name;
      (this.patientBillingInformation as any[])[this.selectedRowIndex].payerId = this.selectedPayer.id;
      (this.patientBillingInformation as any[])[this.selectedRowIndex].billTypeId = this.selectedBillType.id;
      (this.patientBillingInformation as any[])[this.selectedRowIndex].billType = this.selectedBillType.name;
      (this.patientBillingInformation as any[])[this.selectedRowIndex].insuranceId = this.insuranceId.toUpperCase();
      (this.patientBillingInformation as any[])[this.selectedRowIndex].groupNumber = this.groupNumber ? this.groupNumber.toUpperCase() : '';
      this.selectedPayer = undefined;
      this.selectedBillType = undefined;
      this.insuranceId = '';
      this.groupNumber = '';
      this.selectedRows = [];
      this.patientBillingInfoChanged.emit(this.patientBillingInformation);
    }
    this.refresh();
  }

  public payerChanged(event: any) {
    this.selectedPayer = event.selectedRowsData[0];
    if (this.selectedPayer)
      this.payerSelector?.instance.close();
    this.selectionChange(undefined);
  }

  public selectionChange(e?: any[]) {
    setTimeout(() => {
      this.changeStateButtons();
    }, 1000);
    this.loadInfoToUpdate(e);
  }

  public loadInfoToUpdate(items?: Array<any>) {
    if (items === undefined) {
      return;
    }
    if (items.length === 1) {
      this.insuranceId = items[0].insuranceId;
      this.groupNumber = items[0].groupNumber;
      this.selectedBillType = {
        id: items[0].billTypeId,
        name: items[0].billType,
        allPayers: items[0].allPayers ? items[0].allPayers : false,
      } as BillType;
      const tempPayer = {
        id: items[0].payerId,
        name: items[0].payer,
      } as Payer;
      this.payersDataSource.next(new DataSource({
        store: this.billingInfoService.payerCustomStore(this.selectedBillType.id, this.selectedBillType.allPayers, this.payersDataSource)
      }));
      this.payersDataSource.value.load()
        .then(() => {
          this.selectedPayer = tempPayer;
          this.refresh();
        }).catch(e => Sentry.captureException(e));
    } else {
      this.selectedBillType = undefined;
      this.selectedPayer = undefined;
      this.insuranceId = '';
      this.groupNumber = '';
    }
    this.refresh();
  }

  public changeStateButtons() {
    this.responsiveButtons = [{
      name: 'Add',
      icon: 'add',
      disabled: this.insuranceId === '' || this.selectedPayer === undefined || this.selectedBillType === undefined,
    }, {
      name: 'Remove',
      icon: 'trash',
      disabled: this.selectedRows.length === 0,
    }, {
      name: 'Up',
      icon: 'arrowup',
      disabled: this.disableUp,
    }, {
      name: 'Down',
      icon: 'arrowdown',
      disabled: this.disableDown,
    },
    ];
  }

  public refresh() {
    if (this.ref) {
      this.ref.detectChanges();
    }
  }

  public get disableUp() {
    return this.selectedRows.length !== 1 || this.patientBillingInformation === undefined || (this.patientBillingInformation as any[]).length <= 1 ||
      (this.selectedRows[0] as any).order === 1;
  }

  public get disableDown() {
    return this.selectedRows.length !== 1 || this.patientBillingInformation === undefined || (this.patientBillingInformation as any[]).length <= 1 ||
      (this.selectedRows[0] as any).order === (this.patientBillingInformation as any[]).length;
  }

  public ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
